import React, {useEffect, useState, Suspense} from 'react';
import {useLocation} from 'react-router-dom';
import {QueryClientProvider} from '@tanstack/react-query';
import {I18nextProvider, useTranslation} from 'react-i18next';
import i18n from 'i18n';
import {AuthProvider} from './contexts/auth';
import GlobalStyles from '../GlobalStyles';
import CenterContent from 'components/CenterContent';
import Box from 'components/Box';
import {BackLinkProvider} from 'hooks/useBackLink';
import {breakpoints} from 'styles/globals';
import {BreakpointsProvider} from 'hooks/useBreakpoints';
import Spinner from 'components/Spinner';
import WebAppInstallPromptProvider from './shared/webAppInstallPrompt/context';
import Checks from '../../compat/checks';
import Compatibility from '../../compat/compatibility';
import useLocalStorage from 'hooks/useLocalStorage';
import {ServerEventsProvider} from './contexts/serverEvents';
import {ModalManager, BodyScrollLock} from 'components/Modal';
import PlaylistCompletionProvider from 'components/PlaylistCompletionProvider';
import {PageTitleManager} from 'components/PageTitle';
import queryparse from 'tricks/string/queryparse';
import queryClient from '../lib/queryClient';
import PageThemeProvider from './PageThemeProvider';
import {stripRedirectFromLocation} from 'utils/stripRedirectFromLocation';
import useTimeout from 'hooks/useTimeout';
import {AppNameContextProvider} from 'hooks/useAppNameContext';
import Status from 'components/Status';

function BackButtonAndPageTitleProviders({children}) {
	const [t] = useTranslation();
	const location = useLocation();
	const {pathname, search, state} = location;
	const {asset, page} = queryparse(search);

	return (
		<BackLinkProvider
			location={stripRedirectFromLocation(location)}
			track={[pathname, asset, page, 'week-commencing']}
			wasRedirected={state?.wasRedirected}
		>
			<PageTitleManager
				syncDocumentTitle
				baseTitle={t('hub_title')}
				pathname={pathname}
			>
				{children}
			</PageTitleManager>
		</BackLinkProvider>
	);
}

function LoadingIndicator({id}) {
	const [loadingMessage, setLoadingMessage] = useState(false);

	useTimeout(() => {
		setLoadingMessage(i18n.t('loading_too_slow'));
	}, 6000);

	return (
		<CenterContent fillParent id={id}>
			<Box align="center">
				<Spinner />
				<p>{i18n.t('loading')}</p>
				<Status>{loadingMessage && <p>{loadingMessage}</p>}</Status>
			</Box>
		</CenterContent>
	);
}

function AppWrapper({children}) {
	const [featureTestsPassed, setFeatureTestsPassed] = useState(null);
	const [featureTestsIgnore, setFeatureTestsIgnore] =
		useLocalStorage('featureTestsIgnore');

	useEffect(() => {
		// Get the browser feature detection
		Checks().then(tests => {
			// Have all tests passed?
			const featureTestsPassed = tests.every(([, err]) => !err);
			setFeatureTestsPassed(featureTestsPassed);
		});
	}, []);

	const passCompatibility = () => {
		setFeatureTestsIgnore(true);
	};

	return (
		// Report Result of tests and render warning page...
		!featureTestsPassed && !featureTestsIgnore ? (
			featureTestsPassed === null ? (
				// Wait until this has loaded
				<PageThemeProvider>
					<GlobalStyles />
					<LoadingIndicator id="app" />
				</PageThemeProvider>
			) : (
				<PageThemeProvider>
					<BodyScrollLock id="app">
						<GlobalStyles />
						<Compatibility onContinue={passCompatibility} />
					</BodyScrollLock>
				</PageThemeProvider>
			)
		) : (
			<AppNameContextProvider appName="hub">
				<PageThemeProvider>
					<Suspense fallback={<LoadingIndicator id="app" />}>
						<QueryClientProvider client={queryClient}>
							<I18nextProvider i18n={i18n}>
								<WebAppInstallPromptProvider>
									<BreakpointsProvider
										breakpoints={breakpoints}
									>
										<ModalManager>
											<BackButtonAndPageTitleProviders>
												<PlaylistCompletionProvider>
													<AuthProvider>
														<ServerEventsProvider>
															<GlobalStyles />
															<BodyScrollLock id="app">
																{children}
															</BodyScrollLock>
														</ServerEventsProvider>
													</AuthProvider>
												</PlaylistCompletionProvider>
											</BackButtonAndPageTitleProviders>
										</ModalManager>
									</BreakpointsProvider>
								</WebAppInstallPromptProvider>
							</I18nextProvider>
						</QueryClientProvider>
					</Suspense>
				</PageThemeProvider>
			</AppNameContextProvider>
		)
	);
}

export default AppWrapper;
